/**
 * Robust "copy text to clipboard" helper.
 *
 * The async Clipboard API (`navigator.clipboard.writeText`) is the happy path,
 * but it rejects in two situations we hit in production:
 *
 *  1. The packaged Electron shell gates `clipboard-sanitized-write` through its
 *     `setPermissionCheckHandler` (electron/main.js). Until a desktop build
 *     ships the grant (OSPROD-166), the renderer's `writeText` throws
 *     `DOMException: Failed to execute 'writeText' on 'Clipboard': Write
 *     permission denied`.
 *  2. A plain browser rejects `writeText` when the document isn't focused or
 *     lacks a transient user activation (e.g. a copy fired right after an
 *     `await`).
 *
 * In both cases we fall back to the legacy `document.execCommand('copy')` over a
 * hidden <textarea>. That path has no permission gate and works inside any user
 * gesture, so click-to-copy keeps working in already-shipped Electron builds via
 * a normal web deploy — no desktop rebuild required.
 *
 * Returns whether the text actually reached the clipboard, so callers can show a
 * real success/failure state instead of optimistically flashing "Copied".
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Permission denied / not focused — fall through to the legacy path.
  }
  return legacyCopy(text)
}

function legacyCopy(text: string): boolean {
  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    // Off-screen, non-interactive, but still selectable.
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.top = '-9999px'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    textarea.setSelectionRange(0, text.length)
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    return ok
  } catch {
    return false
  }
}
